<template>
  <div class="ra-question-table">
    <p
      v-if="previousCompletedAtLabel"
      class="ra-question-table__previous-at"
    >
      前回入力: {{ previousCompletedAtLabel }}
    </p>
    <!-- ヘッダー行は設けず、各セル内のラベルで列の意味を示す（ui-design.md 4.1）。 -->
    <table class="ra-question-table__table">
      <colgroup>
        <col
          v-for="(width, index) in columnWidths"
          :key="index"
          :style="{ width }"
        >
      </colgroup>
      <tbody>
        <RiskAssessmentQuestionRow
          v-for="row in rows"
          :key="row.question.questionNo"
          :question="row.question"
          :draft="draftMap[row.question.questionNo] ?? null"
          :previous-answer="previousAnswerMap[row.question.questionNo] ?? null"
          :category-label="row.categoryLabel"
          :category-row-span="row.categoryRowSpan"
          :disabled="disabled"
          @select-answer-type="(answerType) => emit('select-answer-type', row.question.questionNo, answerType)"
          @toggle-detail="(detailKey) => emit('toggle-detail', row.question.questionNo, detailKey)"
        />
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type {
  RiskAssessmentAnswer,
  RiskAssessmentAnswerType,
  RiskAssessmentQuestion,
  RiskAssessmentVersion
} from "@/vue/entities/risk-assessment";
import { formatDateTime } from "../model/datetime";
import type { AnswerDraft } from "../model/types";
import RiskAssessmentQuestionRow from "./RiskAssessmentQuestionRow.vue";

// カテゴリ / 設問 / 該当 / 非該当 / 不明 / 前回入力（ui-design.md 4.3 の実寸比）。
const COLUMN_WIDTHS = ["10%", "25%", "32%", "10%", "10%", "13%"];
const COLUMN_WIDTHS_WITHOUT_CATEGORY = ["27%", "36%", "11%", "11%", "15%"];

type QuestionRowModel = {
  question: RiskAssessmentQuestion;
  categoryLabel: string | null;
  categoryRowSpan: number;
};

const props = withDefaults(
  defineProps<{
    version: RiskAssessmentVersion;
    draftMap: Record<number, AnswerDraft>;
    previousAnswerMap: Record<number, RiskAssessmentAnswer>;
    previousCompletedAt?: string | null;
    disabled?: boolean;
  }>(),
  {
    previousCompletedAt: null,
    disabled: false
  }
);

const emit = defineEmits<{
  "select-answer-type": [number, RiskAssessmentAnswerType];
  "toggle-detail": [number, string];
}>();

const hasCategories = computed<boolean>(() => props.version.categories.length > 0);

const columnWidths = computed<string[]>(() =>
  hasCategories.value ? COLUMN_WIDTHS : COLUMN_WIDTHS_WITHOUT_CATEGORY
);

// 同一カテゴリの先頭行だけがカテゴリセルを持ち、rowspan で結合する。
const rows = computed<QuestionRowModel[]>(() => {
  const questionMap = new Map(props.version.questions.map((question) => [question.questionNo, question]));
  const groups = hasCategories.value
    ? props.version.categories.map((category) => ({ label: category.label, questionNos: category.questionNos }))
    : [{ label: null, questionNos: props.version.questions.map((question) => question.questionNo) }];

  return groups.flatMap((group) => {
    const questions = group.questionNos
      .map((questionNo) => questionMap.get(questionNo))
      .filter((question): question is RiskAssessmentQuestion => question !== undefined);

    return questions.map((question, index) => ({
      question,
      categoryLabel: index === 0 ? group.label : null,
      categoryRowSpan: questions.length
    }));
  });
});

const previousCompletedAtLabel = computed<string>(() => formatDateTime(props.previousCompletedAt));
</script>

<style lang="scss" scoped>
.ra-question-table__previous-at {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 4px;
}

.ra-question-table__table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}
</style>
